// Approved How It Works page content — Oxyvin "How It Works page" (27 Aug 2026).
// Content is verbatim from the approved Word document (plus the exact scale-up/scale-down
// bullet phrasing supplied directly in the approved task brief). Do not alter meaning, claims
// or factual statements without approval — only presentation (headings, grouping, layout).

export const howItWorksPage = {
  seo: {
    title: "How Oxyvin Works | Flexible Customer Engagement",
    description: "See how Oxyvin helps businesses add flexible customer engagement capacity through the right people, processes, technology and operational support.",
  },
  hero: {
    heading: "Customer engagement, without the fixed capacity.",
    lede: "Add the customer engagement capacity you need, when you need it.",
    body: "From understanding your requirement to deploying the right people, technology and operating model, Oxyvin makes it simple to extend your customer engagement organization. Whether you need additional capacity for a campaign, multilingual customer engagement, peak-period support or a longer-term engagement, we build the model around your requirements.",
  },
  sections: [
    {
      kind: "steps",
      heading: "One engagement model. Seven simple steps.",
      items: [
        ["Understand", "Your requirements"],
        ["Design", "The engagement model"],
        ["Deploy", "The right people"],
        ["Connect", "People, process & technology"],
        ["Go Live", "Start customer engagement"],
        ["Measure", "Monitor performance and outcomes"],
        ["Scale", "Up or down as your needs change"],
      ],
    },
    {
      kind: "intro",
      heading: "Start with what you need.",
      itemsIntro: "Every Oxyvin engagement starts with understanding your requirement. We look at:",
      items: ["What you want to achieve", "Customer and prospect volumes", "Type of engagement", "Languages required", "Calling or support requirements", "Existing CRM and technology", "Expected duration", "Required capacity"],
      closing: "You don't need to have the entire operating model figured out before you speak to us. We'll help define it with you.",
    },
    {
      kind: "intro",
      heading: "We design the engagement around your business.",
      paragraphs: ["Based on your requirements, we determine the appropriate combination of:"],
      emphasisLine: "People + Process + Technology + Capacity",
      itemsIntro: "This includes identifying:",
      items: ["The right customer engagement professionals", "Language requirements", "Campaign or support workflows", "Technology requirements", "Reporting and visibility", "Quality processes", "Operating responsibilities"],
      closing: "The result is an engagement model built around your requirements, rather than a fixed package.",
    },
    {
      kind: "intro",
      heading: "Deploy the right people.",
      paragraphs: ["Once the engagement model is agreed, we onboard the required customer engagement professionals."],
      itemsIntro: "Depending on the requirement, this can include professionals with specific:",
      items: ["Language capabilities", "Customer engagement experience", "Industry familiarity", "Campaign skills", "Process knowledge"],
      closing: "Our distributed workforce model allows us to look beyond a single geographic location when finding the right people. This is particularly valuable when a campaign requires regional or multilingual customer engagement capability.",
      readMore: { label: "See how this worked in practice: Building a Multilingual Customer Engagement Workforce for a Financial Services Company", to: "/resources/bfsi-multilingual-customer-engagement" },
    },
    {
      kind: "twoModel",
      heading: "Connect the technology.",
      intro: "Your customer engagement team needs the right operating environment. Depending on the engagement, we can work with:",
      options: [
        ["Your Existing Technology", "Your CRM, dialer, contact-center platform and other systems."],
        ["An Oxyvin-Managed Environment", "Where Oxyvin provides the required technology and operational environment as part of the engagement."],
      ],
      closing: "The objective is simple: Technology should fit the engagement—not the other way around.",
    },
    {
      kind: "intro",
      heading: "Go live.",
      itemsIntro: "Once people, processes and technology are ready, the engagement goes live. We establish:",
      items: ["Campaign workflows", "Calling or support processes", "Scripts and guidelines", "Escalation procedures", "Reporting", "Quality processes", "Operational ownership"],
      closing: "Our typical average go-live is 3 days, depending on the complexity and requirements of the engagement.",
    },
    {
      kind: "intro",
      heading: "Monitor. Measure. Improve.",
      paragraphs: ["Once live, customer engagement shouldn't operate as a black box."],
      itemsIntro: "Depending on the engagement, you can have visibility into:",
      items: ["Campaign activity", "Agent activity", "Login status", "Call activity", "Outcomes", "Historical performance", "Call recordings where enabled", "Operational reports"],
      closing: "This gives your team the visibility required to understand what's happening and make decisions based on actual engagement data.",
    },
    {
      kind: "twoModel",
      heading: "Customer engagement capacity should scale up and down with your needs.",
      options: [
        ["Scale Up", ["Campaign volumes increase", "Seasonal demand rises", "A new market requires additional people", "Additional customer engagement capacity is required"], "When that happens, Oxyvin can help you scale customer engagement capacity up. Add the people and capacity required without having to build a permanent team for a temporary requirement."],
        ["Scale Down", ["A campaign ends", "Peak demand passes", "Requirements change", "Permanent capacity is no longer required"], "When a campaign ends, a peak period passes or your requirement changes, you shouldn't necessarily be left carrying permanent customer engagement capacity. Oxyvin's flexible model allows you to scale down when you need less. That's the difference between adding capacity and building fixed capacity."],
      ],
    },
    {
      kind: "twoModel",
      heading: "What you bring. What Oxyvin provides.",
      options: [
        ["You bring", ["Customer journey", "Business objectives", "Customer data", "CRM and processes, where applicable", "Product and service knowledge", "Brand guidelines", "Desired outcomes"]],
        ["Oxyvin provides", ["Customer engagement professionals", "Multilingual capability", "Contact-center technology", "Campaign execution", "Operational management", "Reporting and visibility", "Flexible customer engagement capacity"]],
      ],
      closing: "Together: Your business expertise + Oxyvin's customer engagement capability creates an extension of your customer engagement organization.",
    },
    {
      kind: "tags",
      heading: "Built around your customer journey.",
      intro: "Oxyvin can support different stages of customer engagement, including:",
      items: ["Lead → Outreach", "Prospect → Qualification", "Customer → Support", "Existing Customer → Follow-up", "Opportunity → Conversion", "Customer Journey → Multichannel engagement"],
      note: "The exact engagement model depends on your business, customers and objectives.",
    },
    {
      kind: "intro",
      heading: "Flexible by design.",
      paragraphs: ["Your requirements may change. Your customer volumes may change. Your language requirements may change. Your campaign may change. Your business may change. Your customer engagement capacity should be able to change with them."],
    },
    {
      kind: "quote",
      text: "Customer engagement capacity should scale up and down with your needs.",
      closing: "That's the principle behind the Oxyvin operating model.",
    },
  ],
  finalCta: {
    heading: "Ready to add customer engagement capacity?",
    text: "Whether you need additional capacity for a campaign, multilingual customer engagement, peak-period support or a longer-term engagement team, Oxyvin can help you build the right model.",
  },
};
